import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { NotificationAvatar, describeWhen } from './notificationPresentation';

/**
 * Uma linha de aviso, usada no menu do sino e na página de notificações.
 * Não lido: fundo azul claro e ponto na direita. Resolvido: etiqueta "Resolvido".
 */
function NotificationListItem({ item, onClick, dense = false }) {
  const unread = !item.is_read && !item.is_resolved;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        px: 2,
        py: dense ? 1.25 : 1.75,
        cursor: 'pointer',
        backgroundColor: unread ? 'rgba(var(--color-primary-rgb), 0.05)' : 'transparent',
        opacity: item.is_resolved ? 0.7 : 1,
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.09)' },
        '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: -2 },
      }}
    >
      <NotificationAvatar type={item.type} size={dense ? 34 : 40} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: unread ? 700 : 500,
              color: 'var(--color-text-dark)',
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.title}
          </Typography>
          {item.is_resolved && (
            <Chip label="Resolvido" size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#E8F5E9', color: '#2E7D32' }} />
          )}
        </Box>

        {item.body && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-medium)',
              mt: 0.25,
              lineHeight: 1.4,
              // no sino o texto é cortado em 2 linhas; na página aparece inteiro
              ...(dense
                ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                : { whiteSpace: 'pre-line' }),
            }}
          >
            {item.body}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: 'var(--color-text-light)', display: 'block', mt: 0.5 }}>
          {describeWhen(item.updated_at || item.created_at)}
        </Typography>
      </Box>

      {unread && (
        <Box
          aria-label="Não lida"
          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--color-primary)', mt: 1, flexShrink: 0 }}
        />
      )}
    </Box>
  );
}

export default NotificationListItem;
